import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { MENU_ITEMS } from '../../constants';
import { TabId } from '../shared/BottomNav';
import { logger } from '../../lib/logger';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  logoUrl: string;
}

/**
 * Sidebar - Desktop/tablet navigation sidebar
 *
 * Features:
 * - Collapsible sidebar with smooth animations
 * - Active tab indicator with layout animations
 * - Logo and branding
 * - Menu items from constants
 * - Responsive sizing (collapsed: 80px, expanded: 280px)
 */
export function Sidebar({ isOpen, onToggle, activeTab, onTabChange, logoUrl }: SidebarProps) {
  return (
    <motion.aside
      initial={false}
      animate={{ width: isOpen ? 280 : 80 }}
      transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
      className="bg-[#5D1828] border-r border-[#7A2235] z-30 flex flex-col flex-shrink-0 shadow-2xl h-screen sticky top-0 overflow-hidden"
    >
      {/* Logo and Brand */}
      <div className="flex-none p-6 flex items-center justify-between">
        <div className={`flex items-center gap-3 ${!isOpen && 'justify-center w-full'}`}>
          <motion.div
            className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg p-1.5 flex-shrink-0"
            layout
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          >
            <img
              src={logoUrl}
              alt="Logo"
              className="w-full h-full object-contain"
            />
          </motion.div>
          <AnimatePresence mode="wait">
            {isOpen && (
              <motion.div
                key="brand-text"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="flex flex-col overflow-hidden whitespace-nowrap"
              >
                <span className="font-bold text-white leading-tight uppercase tracking-wide">Gestor</span>
                <span className="text-xs text-white/60 font-medium">Municipal v7.0</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
        {MENU_ITEMS.map((item) => (
          <motion.button
            key={item.id}
            onClick={() => {
              logger.log('[Sidebar] Menu item clicked:', item.id, 'Current activeTab:', activeTab);
              onTabChange(item.id);
            }}
            layout
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl group relative ${
              activeTab === item.id
                ? 'bg-white/10 text-white shadow-inner'
                : 'text-white/70 hover:bg-white/5 hover:text-white'
            } ${!isOpen ? 'justify-center' : ''}`}
          >
            <div className={`flex-shrink-0 ${
              activeTab === item.id ? 'text-[#B89658]' : 'text-white/70 group-hover:text-white'
            } transition-colors duration-200`}>
              <item.icon size={22} strokeWidth={activeTab === item.id ? 2.5 : 2} />
            </div>

            <AnimatePresence mode="wait">
              {isOpen && (
                <motion.span
                  key={`label-${item.id}`}
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="font-medium tracking-wide overflow-hidden whitespace-nowrap"
                >
                  {item.label}
                </motion.span>
              )}
            </AnimatePresence>

            {activeTab === item.id && (
              <motion.div
                layoutId="activeTabIndicator"
                className="absolute left-0 w-1 h-8 bg-[#B89658] rounded-r-full"
                transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              />
            )}
          </motion.button>
        ))}
      </nav>

      {/* Collapse Toggle Button */}
      <div className="flex-none p-4 border-t border-[#7A2235]">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center p-2 text-white/60 hover:bg-white/5 hover:text-white rounded-lg transition-colors duration-200 overflow-hidden"
        >
          <motion.div
            className="flex items-center gap-2"
            initial={false}
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          >
            <ChevronRight size={isOpen ? 18 : 20} />
          </motion.div>
          <AnimatePresence mode="wait">
            {isOpen && (
              <motion.span
                key="collapse-text"
                initial={{ opacity: 0, width: 0, marginLeft: 0 }}
                animate={{ opacity: 1, width: "auto", marginLeft: 8 }}
                exit={{ opacity: 0, width: 0, marginLeft: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="text-sm font-medium overflow-hidden whitespace-nowrap"
              >
                Contraer menú
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>
    </motion.aside>
  );
}
